/**
 * Worm Placement — Pure functions for randomly placing worms in a container.
 * Uses rejection sampling to enforce minimum spacing and edge padding.
 */

import type { BuriedWorm } from '@/types';

/**
 * Place worms at random positions with minimum spacing enforcement.
 *
 * @param count - Number of worms to place
 * @param width - Container width in px
 * @param height - Container height in px
 * @param minSpacing - Minimum px between worm centers
 * @param edgePadding - Minimum px from container edges
 * @returns Array of BuriedWorm with positions and random rotations
 */
export function placeWorms(
  count: number,
  width: number,
  height: number,
  minSpacing: number,
  edgePadding: number,
): BuriedWorm[] {
  const worms: BuriedWorm[] = [];
  const maxAttempts = count * 50; // prevent infinite loop
  let attempts = 0;

  while (worms.length < count && attempts < maxAttempts) {
    attempts++;

    const x = edgePadding + Math.random() * (width - 2 * edgePadding);
    const y = edgePadding + Math.random() * (height - 2 * edgePadding);

    // Check spacing against all placed worms
    const tooClose = worms.some((w) => {
      const dx = w.x - x;
      const dy = w.y - y;
      return Math.sqrt(dx * dx + dy * dy) < minSpacing;
    });

    if (!tooClose) {
      worms.push({
        id: `worm-${worms.length}`,
        x,
        y,
        rotation: Math.random() * 60 - 30, // -30 to +30 degrees
        revealed: false,
      });
    }
  }

  return worms;
}

/**
 * Calculate distance between two points.
 */
export function distance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}
